// The item actions at the foot of a line's page when it is opened from the script: a new line after it,
// Animate this line, Earlier, Later and Delete. Every button acts at once and closes the page.
import { lineById, markById, moveLine, pictureAt, placeholderOf, removeLine, splitLineOntoOwnPicture } from "../../../model/timeline";
import { Icon } from "../../common";
import { ItemActions } from "../../stages/StagePage";
import { useTimeline } from "../TimelineLoader";
import type { PageProps } from "./index";

export function LineActions({ lineId, onClose, onOpen, onSeek }: { lineId: string } & Pick<PageProps, "onClose" | "onOpen" | "onSeek">) {
  const { ep, update } = useTimeline();
  const line = lineById(ep, lineId);
  if (!line) return null;
  const index = ep.lines.findIndex(b => b.id === lineId);
  const silent = line.how === "silent";
  const noun = silent ? "silence" : "line";
  // The next line, if it is the untouched silence of its picture: "Line after" fills that instead of adding another.
  const next = ep.lines[index + 1] && lineById(ep, ep.lines[index + 1]!.id);
  const nextPic = next && pictureAt(ep, next.start);
  const after = next && nextPic && placeholderOf(ep, nextPic.id)?.id === next.id ? next : undefined;
  const move = (dir: -1 | 1) => {
    const moved = moveLine(ep, lineId, dir);
    update(e => moveLine(e, lineId, dir));
    const start = lineById(moved, lineId)?.start;
    if (start !== undefined) onSeek(start); // the playhead follows the line
    onClose();
  };
  // Gives the line its own copy of the image showing there (one undoable step), then opens that image's Animate stage.
  const animate = () => {
    const out = splitLineOntoOwnPicture(ep, lineId);
    if (!out.pictureId) return;
    if (out.ep !== ep) update(() => out.ep);
    const t = markById(out.ep, "picture", out.pictureId)?.t ?? line.start;
    onClose();
    onOpen("picture", { t, editId: out.pictureId, step: "animate" });
  };
  return (
    <ItemActions title={`This ${noun}`}>
      {/* a little before the end, so the new line goes after this one and not after the next one */}
      <button className="btn" onClick={() => { onClose(); onOpen("line", after ? { t: after.start, editId: after.id } : { t: Math.max(line.start, line.end - 0.05) }); }}><Icon name="plus" size={18} /> Line after</button>
      {pictureAt(ep, line.start) && <button className="btn" onClick={animate}><Icon name="sparkle" size={18} /> Animate this {noun}</button>}
      <button className="btn" disabled={index <= 0} onClick={() => move(-1)}><Icon name="back" size={18} /> Earlier</button>
      <button className="btn" disabled={index >= ep.lines.length - 1} onClick={() => move(1)}>Later <Icon name="chevron" size={18} /></button>
      <button className="btn danger" aria-label={`Delete ${noun}`} title={`Delete ${noun}`} onClick={() => { update(e => removeLine(e, lineId)); onClose(); }}><Icon name="trash" size={20} /></button>
    </ItemActions>
  );
}
